import * as d3 from 'd3';
import { STYLES } from '../../styles/constants';

interface Position {
  x: number;
  y: number;
}

interface RowContextMenuCallbacks {
  onAddRowUp: () => void;
  onAddRowDown: () => void;
  onMoveRowUp: () => void;
  onMoveRowDown: () => void;
  onDeleteRow: () => void;
}

// Fonction pour obtenir le scale actuel depuis le container swimlane
function getCurrentScale(): number {
  const swimlaneContainer = document.getElementById('swimlane-container');
  if (swimlaneContainer) {
    const transform = window.getComputedStyle(swimlaneContainer).transform;
    if (transform && transform !== 'none') {
      // Extraire le scale depuis la matrice de transformation
      // transform: scale(x) donne une matrice comme "matrix(x, 0, 0, x, 0, 0)"
      const matrix = transform.match(/matrix\(([^)]+)\)/);
      if (matrix) {
        const values = matrix[1].split(',').map(v => parseFloat(v.trim()));
        if (values.length >= 1) {
          // La première valeur de la matrice est le scaleX
          const scaleValue = values[0];
          if (!isNaN(scaleValue) && scaleValue > 0) {
            return scaleValue;
          }
        }
      }
      // Si c'est un scale() simple, essayer de l'extraire
      const scaleMatch = transform.match(/scale\(([^)]+)\)/);
      if (scaleMatch) {
        const scaleValue = parseFloat(scaleMatch[1]);
        if (!isNaN(scaleValue) && scaleValue > 0) {
          return scaleValue;
        }
      }
    }
  }
  return 1; // Par défaut, pas de zoom
}

export function createRowContextMenu(
  container: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  position: Position,
  callbacks: RowContextMenuCallbacks,
  t: (key: string) => string,
  rowIndex?: number,
  totalRows?: number
) {
  // Remove any existing context menus
  d3.selectAll('.row-context-menu').remove();

  // Obtenir le scale actuel du container swimlane
  const currentScale = getCurrentScale();

  // Utiliser position: fixed pour éviter d'être coupé par les conteneurs parents
  // Les coordonnées sont déjà en coordonnées de page (pageX, pageY)
  const menuX = position.x;
  const menuY = position.y;

  // Ajouter le menu directement au body pour éviter tout problème de découpage
  const menu = d3.select('body')
    .append('div')
    .attr('class', 'row-context-menu')
    .style('position', 'fixed')
    .style('left', `${menuX}px`)
    .style('top', `${menuY}px`)
    .style('background-color', 'white')
    .style('border', `1px solid ${STYLES.colors.border}`)
    .style('border-radius', '4px')
    .style('box-shadow', '0 2px 8px rgba(0,0,0,0.1)')
    .style('min-width', '180px')
    .style('z-index', '10000') // Z-index très élevé pour être au-dessus de tout
    .style('pointer-events', 'auto')
    .style('transform', `scale(${currentScale})`) // Appliquer le zoom au menu
    .style('transform-origin', 'top left'); // Origine de transformation en haut à gauche

  // Add menu items
  addMenuItem(menu, t('features.swimlane.addRowUp'), '⬆️', callbacks.onAddRowUp);
  addMenuItem(menu, t('features.swimlane.addRowDown'), '⬇️', callbacks.onAddRowDown);
  addDivider(menu);
  
  // Déterminer si les boutons de déplacement doivent être désactivés
  const canMoveUp = rowIndex !== undefined && rowIndex > 0;
  const canMoveDown = rowIndex !== undefined && totalRows !== undefined && rowIndex < totalRows - 1;
  
  // Utiliser des icônes différentes pour distinguer "Ajouter" et "Déplacer"
  // Flèches simples pour "Déplacer" vs flèches doubles pour "Ajouter"
  addMenuItem(menu, t('features.swimlane.moveRowUp'), '↑', callbacks.onMoveRowUp, false, !canMoveUp);
  addMenuItem(menu, t('features.swimlane.moveRowDown'), '↓', callbacks.onMoveRowDown, false, !canMoveDown);
  addDivider(menu);
  addDeleteMenuItem(menu, t('features.swimlane.deleteRow'), callbacks.onDeleteRow);

  // Close menu when clicking outside
  d3.select('body').on('click.row-context-menu', () => {
    menu.remove();
  });

  // Prevent menu from closing when clicking inside
  menu.on('click', (event) => {
    event.stopPropagation();
  });

  // Adjust menu position if it goes off screen (après le rendu pour avoir les dimensions réelles)
  // Note: getBoundingClientRect() retourne les dimensions réelles après le scale
  setTimeout(() => {
    const menuNode = menu.node();
    if (menuNode) {
      const menuRect = menuNode.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      let adjustedX = menuX;
      let adjustedY = menuY;

      // Ajuster horizontalement si nécessaire
      // Les dimensions retournées par getBoundingClientRect() sont déjà zoomées
      if (menuRect.right > viewportWidth) {
        adjustedX = menuX - (menuRect.width / currentScale); // Diviser par scale car on utilise left/top en pixels non-zoomés
      }
      if (adjustedX < 0) {
        adjustedX = 10; // Marge de sécurité
      }

      // Ajuster verticalement si nécessaire
      if (menuRect.bottom > viewportHeight) {
        adjustedY = menuY - (menuRect.height / currentScale); // Diviser par scale car on utilise left/top en pixels non-zoomés
      }
      if (adjustedY < 0) {
        adjustedY = 10; // Marge de sécurité
      }

      // Appliquer les ajustements si nécessaire
      if (adjustedX !== menuX || adjustedY !== menuY) {
        menu
          .style('left', `${adjustedX}px`)
          .style('top', `${adjustedY}px`);
      }
    }
  }, 0);
}

function addMenuItem(
  menu: d3.Selection<HTMLDivElement, unknown, HTMLElement | null, any>,
  label: string,
  icon: string,
  onClick: () => void,
  isDanger: boolean = false,
  disabled: boolean = false
) {
  const item = menu
    .append('div')
    .style('padding', '8px 12px')
    .style('display', 'flex')
    .style('align-items', 'center')
    .style('gap', '8px')
    .style('cursor', disabled ? 'not-allowed' : 'pointer')
    .style('color', disabled ? '#999' : (isDanger ? '#dc2626' : '#1f2937'))
    .style('opacity', disabled ? '0.5' : '1')
    .style('transition', 'all 0.2s')
    .style('user-select', 'none')
    .on('mouseenter', function () {
      if (!disabled) {
        d3.select(this)
          .style('background-color', isDanger ? '#fee2e2' : '#f3f4f6')
          .style('color', isDanger ? '#dc2626' : '#111827');
      } else {
        // Même en disabled, montrer un léger feedback visuel
        d3.select(this).style('background-color', '#f9fafb');
      }
    })
    .on('mouseleave', function () {
      d3.select(this)
        .style('background-color', 'white')
        .style('color', disabled ? '#999' : (isDanger ? '#dc2626' : '#1f2937'));
    })
    .on('click', () => {
      if (!disabled) {
        onClick();
        menu.remove();
      }
    });

  item
    .append('span')
    .style('font-size', '14px')
    .style('opacity', disabled ? '0.6' : '1')
    .text(icon);

  item
    .append('span')
    .style('font-size', '13px')
    .style('font-weight', disabled ? 'normal' : '400')
    .text(label);
}

function addDeleteMenuItem(
  menu: d3.Selection<HTMLDivElement, unknown, HTMLElement | null, any>,
  label: string,
  onClick: () => void
) {
  const item = menu
    .append('div')
    .style('padding', '8px 12px')
    .style('display', 'flex')
    .style('align-items', 'center')
    .style('gap', '8px')
    .style('cursor', 'pointer')
    .style('color', '#dc2626')
    .style('transition', 'all 0.2s')
    .style('user-select', 'none')
    .on('mouseenter', function () {
      d3.select(this)
        .style('background-color', '#fee2e2')
        .style('color', '#dc2626');
    })
    .on('mouseleave', function () {
      d3.select(this)
        .style('background-color', 'white')
        .style('color', '#dc2626');
    })
    .on('click', () => {
      onClick();
      menu.remove();
    });

  // Icône SVG de poubelle rouge
  item
    .append('svg')
    .attr('width', '14')
    .attr('height', '14')
    .attr('viewBox', '0 0 24 24')
    .attr('fill', 'none')
    .attr('stroke', '#dc2626')
    .attr('stroke-width', '2')
    .attr('stroke-linecap', 'round')
    .attr('stroke-linejoin', 'round')
    .html('<path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>');

  item
    .append('span')
    .style('font-size', '13px')
    .style('font-weight', '400')
    .text(label);
}

function addDivider(menu: d3.Selection<HTMLDivElement, unknown, HTMLElement | null, any>) {
  menu
    .append('div')
    .style('height', '1px')
    .style('background-color', STYLES.colors.border)
    .style('margin', '4px 0');
}